import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { fetchCompleteSpotData } from 'actions/spot';
import { getSpotDetails } from 'selectors/spot';
import { useIsInternalUser } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'stores/hooks';

/**
 * Custom hook that syncs spot data when the router's spotId changes
 * This handles the case where shallow routing (like browser back/forward)
 * doesn't trigger getInitialProps but we need fresh data
 */
export const useSpotDataSync = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const currentSpot = useAppSelector(getSpotDetails);
  const includeInternalCams = useIsInternalUser();

  // Track the current spot ID to detect changes
  const currentSpotIdRef = useRef<string | null>(currentSpot?._id || null);
  const isNavigatingRef = useRef(false);
  const routeChangeStartRef = useRef(false);

  useEffect(() => {
    const urlSpotId = router.query.spotId as string;

    // Only proceed if router is ready and we have a spot ID
    if (!router.isReady || !urlSpotId) return;

    // Skip if this is the initial load or if we're already navigating
    if (!currentSpotIdRef.current || isNavigatingRef.current) {
      currentSpotIdRef.current = urlSpotId;
      return;
    }

    // Check if the spot ID in the URL is different from current spot
    if (urlSpotId !== currentSpotIdRef.current && urlSpotId !== currentSpot?._id) {
      // Set navigating flag before starting data fetch
      isNavigatingRef.current = true;
      currentSpotIdRef.current = urlSpotId;

      // Parse cookies for client-side requests
      const cookies = document.cookie.split(';').reduce(
        (acc, cookie) => {
          const trimmed = cookie.trim();
          const [key, ...valueParts] = trimmed.split('=');
          if (key && valueParts.length > 0) {
            acc[key] = valueParts.join('=');
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      // Use our new Redux action to fetch all spot data
      dispatch(
        fetchCompleteSpotData(
          urlSpotId,
          cookies,
          '', // clientIp - not available client-side
          '', // countryCode - not available client-side
          () => {
            // Only reset navigating flag after route change is complete
            if (!routeChangeStartRef.current) {
              isNavigatingRef.current = false;
            }
          },
          undefined, // includeLineupCams - falls back to getTreatmentFromWindow
          includeInternalCams || undefined, // includeInternalCams - only pass true for internal users
        ),
      ).catch((error) => {
        console.error('Error during spot data sync:', error);
        isNavigatingRef.current = false;
        routeChangeStartRef.current = false;
      });
    } else if (urlSpotId === currentSpot?._id) {
      // If we're already on the right spot, just update our ref
      currentSpotIdRef.current = urlSpotId;
    }
  }, [router.query.spotId, router.isReady, currentSpot?._id, dispatch, includeInternalCams]);

  // Handle route change events
  useEffect(() => {
    const handleRouteChangeStart = () => {
      routeChangeStartRef.current = true;
    };

    const handleRouteChangeComplete = () => {
      routeChangeStartRef.current = false;
      isNavigatingRef.current = false;
    };

    const handleRouteChangeError = () => {
      routeChangeStartRef.current = false;
      isNavigatingRef.current = false;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router]);

  // Clean up refs when the hook unmounts
  useEffect(
    () => () => {
      isNavigatingRef.current = false;
      currentSpotIdRef.current = null;
      routeChangeStartRef.current = false;
    },
    [],
  );
};

export default useSpotDataSync;
